import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Star, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import SubmitReview from "./SubmitReview";

interface PendingReviewsProps {
  currentUserId: string;
}

type CompletedBooking = {
  id: string;
  start_at: string;
  end_at: string;
  renter_id: string;
  listings: {
    id: string;
    title: string;
    owner_id: string;
  };
  renter_profile?: {
    name: string | null;
  };
  existing_reviews: {
    review_type: string;
  }[];
};

export default function PendingReviews({ currentUserId }: PendingReviewsProps) {
  const [showReviewForm, setShowReviewForm] = useState<{
    bookingId: string;
    reviewType: 'guest_to_host' | 'host_to_guest';
    listingTitle: string;
    targetUserName?: string;
  } | null>(null);

  // Get completed bookings where user was involved and reviews are missing
  const { data: completedBookings, refetch } = useQuery({
    queryKey: ["pendingReviews", currentUserId],
    queryFn: async () => {
      // Get bookings where user was either renter or host, status is completed, and ended more than 1 day ago
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      // Split queries to avoid 400 error with complex OR
      const [asRenter, asHost] = await Promise.all([
        // User as renter
        supabase
          .from("bookings")
          .select(`
            id, start_at, end_at, renter_id,
            listings!inner(id, title, owner_id),
            users(name),
            reviews(review_type)
          `)
          .eq("status", "confirmed")
          .eq("renter_id", currentUserId)
          .lt("end_at", oneDayAgo.toISOString())
          .order("end_at", { ascending: false }),
        // User as host
        supabase
          .from("bookings")
          .select(`
            id, start_at, end_at, renter_id,
            listings!inner(id, title, owner_id),
            users(name),
            reviews(review_type)
          `)
          .eq("status", "confirmed")
          .eq("listings.owner_id", currentUserId)
          .lt("end_at", oneDayAgo.toISOString())
          .order("end_at", { ascending: false })
      ]);

      if (asRenter.error || asHost.error) {
        throw asRenter.error || asHost.error;
      }

      const allBookings = [...(asRenter.data || []), ...(asHost.data || [])];

      // Filter to only show bookings where user hasn't submitted their review yet
      const pendingReviews = allBookings.filter((booking: any) => {
        const isRenter = booking.renter_id === currentUserId;
        const isHost = booking.listings.owner_id === currentUserId;
        
        const existingReviews = booking.reviews || [];
        const hasRenterReview = existingReviews.some((r: any) => r.review_type === 'guest_to_host');
        const hasHostReview = existingReviews.some((r: any) => r.review_type === 'host_to_guest');
        
        // Show if user is renter and hasn't reviewed, or user is host and hasn't reviewed
        return (isRenter && !hasRenterReview) || (isHost && !hasHostReview);
      });

      return pendingReviews.map((booking: any) => ({
        ...booking,
        renter_profile: booking.users,
        existing_reviews: booking.reviews || []
      })) as CompletedBooking[];
    },
    enabled: !!currentUserId,
  });

  if (!completedBookings?.length) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-warning" />
            Ventende anmeldelser
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {completedBookings.map((booking) => {
            const isRenter = booking.renter_id === currentUserId;
            const isHost = booking.listings.owner_id === currentUserId;
            
            const hasRenterReview = booking.existing_reviews.some(r => r.review_type === 'guest_to_host');
            const hasHostReview = booking.existing_reviews.some(r => r.review_type === 'host_to_guest');
            
            const endDate = format(new Date(booking.end_at), "d. MMM yyyy", { locale: da });
            
            return (
              <div key={booking.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">{booking.listings.title}</h4>
                    <p className="text-sm text-muted-foreground">Afsluttet {endDate}</p>
                  </div>
                  <Badge variant="outline">Afsluttet</Badge>
                </div>
                
                <div className="flex gap-2">
                  {isRenter && !hasRenterReview && (
                    <Button
                      size="sm"
                      onClick={() => setShowReviewForm({
                        bookingId: booking.id,
                        reviewType: 'guest_to_host',
                        listingTitle: booking.listings.title
                      })}
                    >
                      <Star className="h-4 w-4 mr-1" />
                      Bedøm plads
                    </Button>
                  )}
                  
                  {isHost && !hasHostReview && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowReviewForm({
                        bookingId: booking.id,
                        reviewType: 'host_to_guest',
                        listingTitle: booking.listings.title,
                        targetUserName: booking.renter_profile?.name || undefined
                      })}
                    >
                      <User className="h-4 w-4 mr-1" />
                      Bedøm lejer
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {showReviewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="max-w-md w-full">
            <SubmitReview
              bookingId={showReviewForm.bookingId}
              listingTitle={showReviewForm.listingTitle}
              reviewType={showReviewForm.reviewType}
              targetUserName={showReviewForm.targetUserName}
              onSubmitted={() => {
                setShowReviewForm(null);
                refetch();
              }}
            />
            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={() => setShowReviewForm(null)}
            >
              Annuller
            </Button>
          </div>
        </div>
      )}
    </>
  );
}